import { forwardRef, useRef } from "react";
import { Calendar } from "lucide-react";

import { useFormat, type Format } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * Hibrit datetime picker — native `<input type="datetime-local">` ile birebir aynı
 * davranışı korur (browser kendi picker'ını açar) ama görsel olarak diğer text input'lar
 * gibi placeholder gösterir.
 *
 * Yapı: gerçek datetime-local input absolute + opacity-0 olarak alt katmanda durur;
 * tıklanabilir alanı tüm component'i kaplar. Üstte placeholder/değer text + takvim
 * iconu render edilir (pointer-events-none, böylece click hep alttaki input'a gider →
 * native picker açılır). Modern tarayıcılarda ek olarak `showPicker()` çağrılır.
 *
 * Value formatı: HTML5 datetime-local sözleşmesi — "YYYY-MM-DDTHH:mm". Form schema'sı
 * bu formatı bekliyor, parent submit'te ISO'ya çevirir (`localInputToIso`).
 */
interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  /**
   * HTML5 datetime-local `min` attribute. Tarayıcı picker'ında öncesi gri görünür ve
   * native form validation'ı tetikler. Format: "YYYY-MM-DDTHH:mm".
   */
  min?: string;
  /** HTML5 datetime-local `max` attribute. */
  max?: string;
  className?: string;
  "aria-invalid"?: boolean;
}

export const DateTimePicker = forwardRef<HTMLInputElement, DateTimePickerProps>(
  function DateTimePicker(
    {
      value,
      onChange,
      onBlur,
      placeholder,
      id,
      disabled,
      min,
      max,
      className,
      "aria-invalid": ariaInvalid,
    },
    forwardedRef,
  ) {
    const internalRef = useRef<HTMLInputElement>(null);
    const t = useT("dateTimePicker");
    const fmt = useFormat();

    const handleClick = () => {
      if (disabled) return;
      const el = internalRef.current;
      if (!el) return;
      el.focus();
      // showPicker() Chrome 99+/Safari 16.4+/Firefox 101+ — tarayıcı default davranışı
      // zaten input'a click ile picker açar, bu sadece programatik garanti.
      el.showPicker?.();
    };

    const display = formatLocalDateTime(value, fmt);

    return (
      <div
        onClick={handleClick}
        data-slot="datetime-picker"
        aria-invalid={ariaInvalid}
        className={cn(
          "relative flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] md:text-sm dark:bg-input/30",
          "focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
          "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          disabled && "pointer-events-none cursor-not-allowed opacity-50",
          !disabled && "cursor-pointer",
          className,
        )}
      >
        <input
          ref={(el) => {
            internalRef.current = el;
            if (typeof forwardedRef === "function") forwardedRef(el);
            else if (forwardedRef) forwardedRef.current = el;
          }}
          type="datetime-local"
          id={id}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          min={min}
          max={max}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
        <Calendar className="pointer-events-none me-2 h-4 w-4 shrink-0 text-muted-foreground" />
        {display ? (
          <span className="pointer-events-none truncate">{display}</span>
        ) : (
          <span className="pointer-events-none truncate text-muted-foreground">
            {placeholder ?? t("placeholder")}
          </span>
        )}
      </div>
    );
  },
);

function formatLocalDateTime(local: string | undefined | null, fmt: Format): string {
  if (!local) return "";
  // datetime-local format: "YYYY-MM-DDTHH:mm[:ss]"
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!match) return "";
  const [, year, month, day, hour, minute] = match.map(Number);
  return fmt.dateTime(new Date(year, month - 1, day, hour, minute), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
